import React from "react";
import { AuthImg, AuthVideo, resolveAuthMediaUrl } from "@/lib/authMedia";

// WHILE YOU WAIT (owner request 2026-10-05): a render is long, and a bar is
// all the customer used to get. This plays something on a slow loop: each item
// holds for `holdMs` (7-10 s), then cross-fades into the next. A clip loops
// under its hold, a still drifts in slowly. Items that just arrived (a scene
// that finished a moment ago) are shown before anything is shown twice, so
// the customer's own video fills in live as it is made.

export type ReelItem = {
  /** Stable across polls: the same scene keeps its key. */
  key: string;
  kind: "video" | "image";
  /** Media URL (the auth-gated proxy path, or blob:/https). */
  src: string;
  /** One short line over the picture, e.g. "Your scenes, as they finish". */
  caption?: string | null;
};

const FADE_MS = 900;

function prefersReducedMotion(): boolean {
  try { return window.matchMedia("(prefers-reduced-motion: reduce)").matches; } catch { return false; }
}

export default function WaitReel({
  items, title, label, holdMs = 8000, testId = "corrido-wait-reel",
}: { items: ReelItem[]; title: string; label: string; holdMs?: number; testId?: string }) {
  const [current, setCurrent] = React.useState<ReelItem | null>(null);
  const [previous, setPrevious] = React.useState<ReelItem | null>(null);
  const seen = React.useRef<Set<string>>(new Set());
  const itemsRef = React.useRef(items);
  itemsRef.current = items;
  const currentRef = React.useRef<ReelItem | null>(null);
  currentRef.current = current;
  const still = React.useMemo(prefersReducedMotion, []);

  /** The next item: the first one never shown, else the one after the current. */
  const pickNext = React.useCallback((): ReelItem | null => {
    const list = itemsRef.current;
    if (!list.length) return null;
    const fresh = list.find((it) => !seen.current.has(it.key));
    if (fresh) return fresh;
    const at = currentRef.current ? list.findIndex((it) => it.key === currentRef.current!.key) : -1;
    return list[(at + 1) % list.length];
  }, []);

  const advance = React.useCallback(() => {
    const next = pickNext();
    if (!next) return;
    if (currentRef.current && next.key === currentRef.current.key) return;
    seen.current.add(next.key);
    setPrevious(currentRef.current);
    setCurrent(next);
  }, [pickNext]);

  // Start as soon as there is something to show; drop the picture if the list
  // empties (a new render with nothing yet).
  React.useEffect(() => {
    if (!items.length) { setCurrent(null); setPrevious(null); return; }
    if (!currentRef.current || !items.some((it) => it.key === currentRef.current!.key)) advance();
  }, [items, advance]);

  React.useEffect(() => {
    if (!current) return;
    const id = window.setTimeout(advance, holdMs);
    return () => window.clearTimeout(id);
  }, [current, holdMs, advance]);

  // The outgoing layer leaves once its fade is over.
  React.useEffect(() => {
    if (!previous) return;
    const id = window.setTimeout(() => setPrevious(null), FADE_MS + 50);
    return () => window.clearTimeout(id);
  }, [previous]);

  // Fetch the next item while this one plays, so the fade lands on a picture.
  React.useEffect(() => {
    if (!current) return;
    const next = pickNext();
    if (next && next.key !== current.key) resolveAuthMediaUrl(next.src).catch(() => { /* retried when shown */ });
  }, [current, pickNext]);

  if (!current) return null;

  const layer = (it: ReelItem, entering: boolean) => (
    <div key={it.key} style={{
      position: "absolute", inset: 0,
      opacity: entering ? 1 : 0,
      animation: entering ? `corridoReelIn ${FADE_MS}ms ease-out` : `corridoReelOut ${FADE_MS}ms ease-in`,
    }}>
      {it.kind === "video" ? (
        <AuthVideo src={it.src} autoPlay muted loop playsInline
          style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
      ) : (
        <AuthImg src={it.src} alt=""
          style={{
            width: "100%", height: "100%", objectFit: "cover", display: "block",
            animation: still ? undefined : `corridoReelDrift ${holdMs + FADE_MS}ms linear forwards`,
          }} />
      )}
    </div>
  );

  return (
    <div data-testid={testId} role="img" aria-label={label} style={{ marginTop: 10 }}>
      <style>{`
        @keyframes corridoReelIn { from { opacity: 0 } to { opacity: 1 } }
        @keyframes corridoReelOut { from { opacity: 1 } to { opacity: 0 } }
        @keyframes corridoReelDrift { from { transform: scale(1) } to { transform: scale(1.08) } }
      `}</style>
      <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 1, textTransform: "uppercase", color: "#94a3b8", marginBottom: 6 }}>
        {title}
      </div>
      <div style={{
        position: "relative", width: "100%", maxWidth: 220, aspectRatio: "9 / 16", maxHeight: 390,
        margin: "0 auto", borderRadius: 10, overflow: "hidden", background: "#05070b", border: "1px solid #2a3648",
      }}>
        {previous && previous.key !== current.key && layer(previous, false)}
        {layer(current, true)}
        {current.caption && (
          <div data-testid={`${testId}-caption`} style={{
            position: "absolute", left: 0, right: 0, bottom: 0, padding: "18px 10px 8px",
            fontSize: 11.5, fontWeight: 700, color: "#ffffff",
            background: "linear-gradient(transparent, rgba(0,0,0,.72))",
          }}>
            {current.caption}
          </div>
        )}
      </div>
    </div>
  );
}
